import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Home as HomeIcon,
  Lock,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { now } from '@/db/ids';
import type { PropertyRow, SubscriptionTier } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';
import { goBackOrTo, goTo } from '@/lib/nav';
import {
  HOME_CAP,
  getActiveProperty,
  getPropertyContentSummary,
  formatContentSummary,
  getSubscriptionTier,
  getSystemsForProperty,
  listProperties,
  setActiveProperty,
} from '@/lib/tasks';

/**
 * /homes — full Home Manager.
 *
 * The HomeSwitcher modal (right-rail, My Home title, ToolsMenu) covers
 * the fast "I just want to flip homes" case. This page is the
 * dedicated dashboard for managing the SET — see every home at a
 * glance, edit any of them, delete any of them, see how close you are
 * to the tier cap, see the active home highlighted in forest.
 *
 * Lives as its own route so the Settings entry doesn't have to grow a
 * messy nested modal, and so the page can hold richer per-row stats
 * later (last-edited date, system count, completion stats) without
 * cramming the switcher.
 */
export default function HomesManager() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const notice = useNotice();
  const [homes, setHomes] = useState<PropertyRow[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tier, setTier] = useState<SubscriptionTier>('paid');
  const [counts, setCounts] = useState<Record<string, number>>({});

  const reload = useCallback(async () => {
    const [list, active, t] = await Promise.all([
      listProperties(db),
      getActiveProperty(db),
      getSubscriptionTier(db),
    ]);
    setHomes(list);
    setActiveId(active?.id ?? null);
    setTier(t);
    // Per-home system count. Cheap enough — one COUNT() per row,
    // typically 1-5 rows. Drives the "12 systems" subtitle so the
    // user can see at a glance which home is more loaded.
    const nextCounts: Record<string, number> = {};
    for (const h of list) {
      const sys = await getSystemsForProperty(db, h.id);
      nextCounts[h.id] = sys.length;
    }
    setCounts(nextCounts);
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  const onSwitch = useCallback(
    async (id: string) => {
      if (id === activeId) return;
      await setActiveProperty(db, id);
      // Wave 49 (user-requested): picking a home means "take me THERE" —
      // land on that home's My Home tab instead of staying on the
      // manager. (Matches the HomeSwitcher popup behavior.)
      goTo(router, '/(tabs)/my-home');
    },
    [db, activeId, router]
  );

  const onEdit = useCallback(
    async (id: string) => {
      // Edit form scopes to active property only — switch first so the
      // form loads the right row, then navigate.
      if (id !== activeId) await setActiveProperty(db, id);
      goTo(router, '/property/edit');
    },
    [router, activeId]
  );

  const onDelete = useCallback(
    async (h: PropertyRow) => {
      if (homes.length <= 1) {
        void notice({
          title: "Can't delete your only home",
          message: 'Add another home first if you want to remove this one.',
          tone: 'warning',
        });
        return;
      }
      // Itemized data-loss summary in the confirm — same shape as the
      // My Home delete dialog so users see the same cost on either
      // surface.
      const summary = await getPropertyContentSummary(db, h.id);
      const summaryLine = formatContentSummary(summary);
      const ok = await confirm({
        title: `Delete ${h.name?.trim() || 'this home'}?`,
        message:
          `You'll permanently lose: ${summaryLine}.\n\n` +
          `This can't be undone. Consider exporting the Home Record File first if you want a printable archive.`,
        confirmLabel: 'Delete home',
        destructive: true,
      });
      if (!ok) return;
      const t = now();
      await db.runAsync(
        `UPDATE property SET deleted_at = ?, updated_at = ? WHERE id = ?`,
        t,
        t,
        h.id
      );
      // Post-delete switching:
      //   - If the deleted home wasn't active, just refresh the list.
      //   - If active was deleted, provisionally flip to the oldest
      //     remaining so the UI has a valid active home. The user
      //     stays on this Home Manager page — the page itself IS the
      //     picker, so a popup picker would be redundant.
      if (activeId === h.id) {
        const remaining = await listProperties(db);
        if (remaining[0]) await setActiveProperty(db, remaining[0].id);
      } else {
        bumpDataRevision();
      }
      await reload();
    },
    [db, homes.length, activeId, confirm, notice, reload]
  );

  const cap = HOME_CAP[tier];
  const atCap = homes.length >= cap;
  const showUpgrade = tier === 'free' && atCap;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/settings')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Home Manager</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }}>
        <View style={styles.header}>
          <Text style={styles.h1}>Your homes</Text>
          <Text style={styles.sub}>
            {homes.length} of {cap} {homes.length === 1 ? 'home' : 'homes'} on file.
            Switch, edit, or remove any of them. Adding another bumps you
            past the cap on the free tier.
          </Text>
        </View>

        <View style={styles.list}>
          {homes.map((h) => {
            const active = h.id === activeId;
            const sysCount = counts[h.id] ?? 0;
            return (
              <View
                key={h.id}
                style={[styles.row, active && styles.rowActive]}>
                <Pressable
                  onPress={() => onSwitch(h.id)}
                  style={styles.rowBody}
                  accessibilityLabel={
                    active
                      ? `${h.name ?? 'Home'} (active)`
                      : `Switch to ${h.name ?? 'home'}`
                  }>
                  <View
                    style={[
                      styles.rowIcon,
                      active && styles.rowIconActive,
                    ]}>
                    <HomeIcon
                      size={18}
                      color={active ? Palette.cream : Palette.forest}
                      strokeWidth={1.8}
                    />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={styles.nameRow}>
                      <Text style={styles.rowName} numberOfLines={1}>
                        {h.name?.trim() || 'Untitled home'}
                      </Text>
                      {active ? (
                        <View style={styles.activeBadge}>
                          <Check size={11} color={Palette.cream} strokeWidth={2.4} />
                          <Text style={styles.activeBadgeText}>Active</Text>
                        </View>
                      ) : null}
                    </View>
                    <Text style={styles.rowAddr} numberOfLines={1}>
                      {h.address?.trim() || h.zip || 'No address set'}
                    </Text>
                    <Text style={styles.rowMeta}>
                      {sysCount} system{sysCount === 1 ? '' : 's'}
                      {h.year_built ? ` · built ${h.year_built}` : ''}
                      {h.sqft ? ` · ${h.sqft.toLocaleString()} sq ft` : ''}
                    </Text>
                  </View>
                </Pressable>

                <View style={styles.rowActions}>
                  <Pressable
                    onPress={() => onEdit(h.id)}
                    hitSlop={6}
                    accessibilityLabel={`Edit ${h.name ?? 'home'}`}
                    style={({ pressed }) => [
                      styles.actionBtn,
                      pressed && { opacity: 0.7 },
                    ]}>
                    <Pencil size={14} color={Palette.forest} strokeWidth={2} />
                  </Pressable>
                  <Pressable
                    onPress={() => onDelete(h)}
                    hitSlop={6}
                    disabled={homes.length <= 1}
                    accessibilityLabel={`Delete ${h.name ?? 'home'}`}
                    style={({ pressed }) => [
                      styles.actionBtn,
                      styles.actionBtnDanger,
                      pressed && { opacity: 0.7 },
                      homes.length <= 1 && { opacity: 0.3 },
                    ]}>
                    <Trash2 size={14} color={Palette.clay} strokeWidth={2} />
                  </Pressable>
                </View>
              </View>
            );
          })}
        </View>

        {showUpgrade ? (
          <View style={styles.upgradeCard}>
            <View style={styles.upgradeIcon}>
              <Lock size={16} color={Palette.cream} strokeWidth={2} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.upgradeTitle}>Upgrade to add more homes</Text>
              <Text style={styles.upgradeSub}>
                Free includes 1 home · Paid adds up to 4 more (vacation,
                family, rentals). Subscription surface lands soon.
              </Text>
            </View>
          </View>
        ) : (
          <Pressable
            onPress={() => goTo(router, '/add-home')}
            disabled={atCap}
            style={({ pressed }) => [
              styles.addBtn,
              pressed && { transform: [{ scale: 0.99 }] },
              atCap && { opacity: 0.45 },
            ]}>
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
            <Text style={styles.addBtnText}>
              {atCap ? `Maximum ${cap} homes reached` : 'Add a home'}
            </Text>
            {!atCap ? (
              <ChevronRight size={16} color={Palette.cream} strokeWidth={2.2} />
            ) : null}
          </Pressable>
        )}

        <View style={styles.footnote}>
          <Text style={styles.footnoteText}>
            Tap a row to make it active. The active home is what every
            other tab shows.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: { fontFamily: Fonts.serif, fontSize: 27, color: Palette.ink, letterSpacing: -0.3 },
  sub: {
    marginTop: 4,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
    lineHeight: 19,
  },

  list: {
    marginTop: Spacing.four,
    marginHorizontal: 18,
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 12,
  },
  rowActive: {
    backgroundColor: '#EAF0EA',
    borderColor: Palette.forest,
  },
  rowBody: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  rowIcon: {
    width: 38, height: 38, borderRadius: 13,
    backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center',
  },
  rowIconActive: {
    backgroundColor: Palette.forest,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  rowName: {
    flex: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 15,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  activeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: Palette.forest,
  },
  activeBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: Palette.cream,
    letterSpacing: 0.3,
  },
  rowAddr: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  rowMeta: {
    marginTop: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.3,
  },
  rowActions: {
    flexDirection: 'row',
    gap: 4,
  },
  actionBtn: {
    width: 32, height: 32, borderRadius: 16,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnDanger: {
    borderColor: 'rgba(176,95,60,0.35)',
    backgroundColor: 'rgba(176,95,60,0.08)',
  },

  addBtn: {
    marginTop: Spacing.four,
    marginHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: Radius.lg,
    backgroundColor: Palette.forest,
  },
  addBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
    letterSpacing: 0.3,
  },

  upgradeCard: {
    marginTop: Spacing.four,
    marginHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: Radius.lg,
    backgroundColor: 'rgba(225,163,61,0.10)',
    borderWidth: 1,
    borderColor: Palette.honeyDeep,
  },
  upgradeIcon: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.honeyDeep,
    alignItems: 'center', justifyContent: 'center',
  },
  upgradeTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  upgradeSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 16,
  },

  footnote: {
    marginTop: Spacing.four,
    marginHorizontal: 22,
    alignItems: 'center',
  },
  footnoteText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 12,
    color: Palette.inkFaint,
    textAlign: 'center',
  },
});
